<template>
  <div>
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Aulas Avulsas</h5>
      <!-- Botão Novo Cadastro -->
      <BButton
        v-if="hasPermission('adicionar_aulas_avulsas')"
        variant="primary"
        class="d-flex align-items-center"
        @click="irParaCadastro"
      >
        <i class="bi bi-plus-circle-fill mr-2" title="Nova Aula"></i>
      </BButton>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3 align-items-center" style="gap: 10px;">
      <span>Filtros:</span>
      <select v-model="filters.quando" class="form-select" @change="filtrar">
        <option value="">Quando</option>
        <option value="Hoje">Hoje</option>
        <option value="Proximas">Próximas</option>
        <option value="Anteriores">Anteriores</option>
        <option value="">Todas</option>
      </select>
      <input
        v-model="filters.aluno"
        placeholder="Aluno"
        class="form-control"
        @input="filtrar"
      />
      <input
        v-model="filters.responsavel"
        placeholder="Responsável"
        class="form-control"
        @input="filtrar"
      />
      <b-button variant="secondary" @click="limparFiltros" class="d-flex align-items-center">
        <i class="bi bi-funnel" title="Limpar Filtros"></i>
      </b-button>
    </div>

    <!-- Tabela customizada -->
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th><button class="btn btn-link p-0" @click="sort('aluno_nome')">Aluno {{ arrow('aluno_nome') }}</button></th>
          <th><button class="btn btn-link p-0" @click="sort('responsavel_nome')">Responsável {{ arrow('responsavel_nome') }}</button></th>
          <th>
            <button class="btn btn-link p-0" @click="sort('datas_contratadas')">
              Datas das Aulas {{ arrow('datas_contratadas') }}
            </button>
          </th>
          <th class="text-end">Ações</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="a in aulasFormatadas" :key="a.id">
          <td>{{ a.aluno_nome }}</td>
          <td>{{ a.responsavel_nome }}</td>
          <td>
            <span v-if="a.datas_contratadas.length">{{ a.datas_contratadas.join(', ') }}</span>
            <span v-else class="text-muted">Nenhuma</span>
          </td>
          <td class="text-end">
            <i v-if="hasPermission('detalhar_aulas_avulsas')" class="bi bi-eye-fill text-primary mx-1"
               @click="detalhar(a.id)" title="Detalhar"></i>
            <i v-if="hasPermission('alterar_aulas_avulsas')" class="bi bi-pencil-fill text-warning mx-1"
               @click="editar(a.id)" title="Editar"></i>
            <i v-if="hasPermission('cancelar_aulas_avulsas')"
              :class="isCancelDisabled(a) ? 'bi bi-x-circle-fill text-secondary mx-1' : 'bi bi-x-circle-fill text-danger mx-1'"
              :style="isCancelDisabled(a) ? 'opacity:.5; cursor:not-allowed' : 'cursor:pointer'"
              @click="!isCancelDisabled(a) && cancelar(a.id)"
              :title="isCancelDisabled(a)
                ? 'Não pode cancelar: há pagamentos realizados'
                : 'Cancelar (remove agendamentos e informações de pagamento)'"></i>
            <router-link v-if="!a.pagamento_pendente"
                         :to="`/operacional/aulas-avulsas/imprimir-recibo/${a.id}`">
              <i class="bi bi-printer-fill text-success mx-1" title="Imprimir Recibo"></i>
            </router-link>
            <i v-if="a.pagamento_pendente" class="bi bi-exclamation-triangle-fill text-warning mx-1"
               title="Pagamento Pendente"></i>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Paginação -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <!-- 1. Resumo -->
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ totalItems }} registros
      </div>

      <!-- 2. Seletor de itens por página -->
      <div class="d-flex align-items-center" style="gap: .5rem;">
        <b-form-select
          v-model="limit"
          :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
          size="sm"
          style="width: 6rem"
          @change="onChangeLimit"
        />
      </div>

      <!-- 3. Controle de página -->
      <div class="d-flex align-items-center">
        <b-pagination
          v-model="page"
          :per-page="limit"
          :total-rows="totalItems"
          size="sm"
          limit="5"
          @input="onPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, getCurrentInstance, watch, ref } from 'vue';
import { useRouter } from 'vue-router';
import { BButton, BPagination, BFormSelect } from 'bootstrap-vue-next';
import { useSortableTable } from '@/utils/useSortableTable';
import axios from '@/services/axios';
import globalUtils from '@/utils/globalUtils.js';
import { usePermissions } from '@/composables/usePermissions';

const router = useRouter();
const { proxy } = getCurrentInstance();
const { fetchPermissions, hasPermission } = usePermissions();

// Composable para listagem de aulas avulsas
const {
  items: aulas,
  totalItems,
  page,
  limit,
  filters,
  load,
  sort,
  arrow,
} = useSortableTable(
  `/operacional/aulas-avulsas`,
  { key: 'datas_contratadas', order: 'asc' }
);

// Opções de limite de página
const pageOptions = ref([10, 15, 20, 25, 50]);

// Computeds para o texto de resumo
const startRow = computed(() =>
  (page.value - 1) * limit.value + 1
);
const endRow = computed(() =>
  Math.min(page.value * limit.value, totalItems.value)
);

// Watchers para recarregar ao mudar página ou limite
watch(page, () => load());
watch(limit, () => {
  page.value = 1;
  load();
});

// Formatação de datas
const aulasFormatadas = computed(() =>
  aulas.value.map(a => ({
    ...a,
    datas_contratadas: Array.isArray(a.datas_contratadas)
      ? a.datas_contratadas.map(d => globalUtils.formatarData(d))
      : []
  }))
);

// Handlers de filtro
function filtrar() {
  page.value = 1;
  load();
}
function limparFiltros() {
  filters.value = { quando: '', aluno: '', responsavel: '' };
  page.value = 1;
  load();
}

// Handlers de paginação
function onChangeLimit() {
  page.value = 1;
  load();
}
function onPageChange() {
  load();
}

function isCancelDisabled(a) {
  // quando NÃO está pendente, significa que há pagamento realizado -> desabilita
  return !a.pagamento_pendente;
}

async function cancelar(id) {
  const ok = await proxy.$confirm(
    'Tem certeza que deseja CANCELAR esta Aula Avulsa?\n\n' +
    'Todos os agendamentos e informações de pagamento serão EXCLUÍDOS definitivamente.',
    'Confirmação'
  );
  if (!ok) return;

  await axios.delete(`/operacional/aulas-avulsas/${id}`);
  proxy.$toast.success('Aula Avulsa cancelada com sucesso!');
  load();
}

// Navegação e ações
function irParaCadastro() {
  router.push('/operacional/aulas-avulsas/novo');
}
function detalhar(id) {
  router.push(`/operacional/aulas-avulsas/${id}/detalhar`);
}
function editar(id) {
  router.push(`/operacional/aulas-avulsas/${id}`);
}

// Inicialização
onMounted(async () => {
  await fetchPermissions();
  filters.value = { quando: '', aluno: '', responsavel: '' };
  load();
});
</script>

<style scoped>
:deep(.pagination.pagination-sm .page-link) {
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}
</style>